// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import 'sweetalert2/dist/sweetalert2.min.css';

import PrivateRoute from './auth/PrivateRoute.js'; // tu guard mejorado por token/exp/roles
import DashboardLayout from './components/nav-bar/DashboardLayout.js';
import { SucursalActivaProvider } from './context/SucursalActivaContext.jsx';
import { useMe } from './hooks/useMe';
import { PERMISO_CORRECCION_EXCEPCIONAL, tienePermiso } from './utils/permisos';

import Finanzas from './components/finanzas/Finanzas.js';
import CuadreTurno from './components/registrar-cierre/CuadreTurno.jsx';
import AperturaTurno from './components/registrar-cierre/AperturaTurno.jsx';
import Caja from './components/registrar-cierre/Caja.jsx';

import Sucursales from './components/sucursales/Sucursales.js';
import Usuarios from './components/usuarios/Usuarios.js';
import Bitacora from './components/bitacora/Bitacora.jsx';

import Agenda from './components/agenda/Agenda.jsx';
import Proveedores from './components/proveedores/Proveedores.jsx';
import Productos from './components/productos/Productos.jsx';
import CuentasPorPagar from './components/cuentas-por-pagar/CuentasPorPagar.jsx';
import Cierres from './components/cierres/Cierres.jsx';
import ComprasPanel from './components/compras/ComprasPanel.jsx';
import GastosPanel from './components/gastos/GastosPanel.jsx';
import Bancos from './components/bancos/Bancos.jsx';
import PagosAdministrativos from './components/pagos-administrativos/PagosAdministrativos.jsx';
import CuentasEntreSucursales from './components/entre-sucursales/CuentasEntreSucursales.jsx';
import Reportes from './components/reportes/Reportes.jsx';
import Resultados from './components/resultados/Resultados.jsx';

import Login from './auth/Login'; // tu login
import AvisoVersion from './components/common/AvisoVersion';
import CambiarContrasena from './auth/CambiarContrasena';

// El cajero OPERA (turno y cuadre); el administrador SUPERVISA y ADMINISTRA.
// La API aplica las mismas restricciones; esto solo evita mostrar pantallas ajenas al rol.
const SoloAdmin = ({ children }) => <PrivateRoute requiredRoles="admin" redirectIfDenied="/Finanzas">{children}</PrivateRoute>;
const SoloCajero = ({ children }) => <PrivateRoute requiredRoles="viewer" redirectIfDenied="/Finanzas/Cierres">{children}</PrivateRoute>;
// Cuadre del cajero; quien tiene el permiso de corrección excepcional entra solo a corregir un cierre concreto (?id=).
function RutaCuadre() {
  const me = useMe();
  const [sp] = useSearchParams();
  return tienePermiso(me, PERMISO_CORRECCION_EXCEPCIONAL) && sp.get('id') ? <CuadreTurno /> : <SoloCajero><CuadreTurno /></SoloCajero>;
}

export default function App() {
  return (
    <>
    {/* Aviso de nueva versión del frontend (independiente de la sesión). */}
    <AvisoVersion />
    <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      {/* Cambio obligatorio de contraseña (DebeCambiarContrasena); requiere sesión */}
      <Route path="/cambiar-contrasena" element={<CambiarContrasena />} />

      {/* Rutas protegidas: Dashboard y anidadas */}
      <Route
        path="/Finanzas"
        element={
          <PrivateRoute>
            <SucursalActivaProvider><DashboardLayout /></SucursalActivaProvider>
          </PrivateRoute>
        }
      >
        {/* Inicio: Agenda para administrador, Caja para cajero. */}
        <Route index element={<Finanzas redirectToAgenda />} />
        <Route path="Dashboard" element={<SoloAdmin><Finanzas /></SoloAdmin>} />

        {/* Cajero: una sola entrada operativa. */}
        <Route path="Caja" element={<SoloCajero><Caja /></SoloCajero>} />
        <Route path="AperturaTurno" element={<SoloCajero><AperturaTurno /></SoloCajero>} />
        <Route path="RegistrarCierre" element={<RutaCuadre />} />
        <Route path="HistorialCuadres" element={<SoloCajero><Navigate to="/Finanzas/Caja" replace /></SoloCajero>} />

        {/* Administrador: supervisión y administración */}
        <Route path="Cierres" element={<SoloAdmin><Cierres /></SoloAdmin>} />
        <Route path="Compras" element={<SoloAdmin><ComprasPanel /></SoloAdmin>} />
        <Route path="Gastos" element={<SoloAdmin><GastosPanel /></SoloAdmin>} />
        <Route path="HistorialPagos" element={<SoloAdmin><Navigate to="/Finanzas/Reportes?r=pagos" replace /></SoloAdmin>} />
        {/* Depósitos forma parte de Bancos */}
        <Route path="Depositos" element={<Navigate to="/Finanzas/Bancos" replace />} />
        <Route path="Bancos" element={<SoloAdmin><Bancos /></SoloAdmin>} />
        <Route path="PagosAdministrativos" element={<SoloAdmin><PagosAdministrativos /></SoloAdmin>} />
        <Route path="CuentasEntreTaquerias" element={<SoloAdmin><CuentasEntreSucursales /></SoloAdmin>} />
        <Route path="Reportes" element={<SoloAdmin><Reportes /></SoloAdmin>} />
        <Route path="Resultados" element={<SoloAdmin><Resultados /></SoloAdmin>} />
        <Route path="Agenda" element={<SoloAdmin><Agenda /></SoloAdmin>} />
        <Route path="Productos" element={<SoloAdmin><Productos /></SoloAdmin>} />
        <Route path="Proveedores" element={<SoloAdmin><Proveedores /></SoloAdmin>} />
        <Route path="CuentasPorPagar" element={<SoloAdmin><CuentasPorPagar /></SoloAdmin>} />
        <Route path="Sucursales" element={<SoloAdmin><Sucursales /></SoloAdmin>} />
        <Route path="Usuarios" element={<SoloAdmin><Usuarios /></SoloAdmin>} />
        <Route path="Bitacora" element={<SoloAdmin><Bitacora /></SoloAdmin>} />

        {/* La captura de pagos se unificó en el cuadre (pestañas Compras y Gastos). */}
        <Route path="RegistrarPagos" element={<Navigate to="/Finanzas/RegistrarCierre" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
